import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Save, Download, Upload, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SHARING_OPTIONS = ["1", "2", "3", "4", "5"];
const DOJ_RE = /^\d{2}-\d{2}-\d{4}$/;

const COLUMNS = [
  { key: "full_name", label: "Full name", ph: "Max 30 characters", maxLength: 30 },
  { key: "joining_date", label: "Date of Joining", sub: "DD-MM-YYYY", ph: "15-08-2025" },
  { key: "room_no", label: "Room No", ph: "101" },
  { key: "floor", label: "Floor Number", ph: "1" },
  { key: "sharing_type", label: "Sharing type", select: SHARING_OPTIONS },
  { key: "monthly_rent", label: "Rent amount", type: "number", ph: "8500" },
  { key: "security_deposit", label: "Deposit (Advance)", type: "number", ph: "15000" },
  { key: "refundable_amount", label: "Refundable amount", type: "number", ph: "≤ deposit" },
  { key: "whatsapp", label: "Mobile number", ph: "+91 98123 40001" },
  { key: "email", label: "Email ID", ph: "tenant@example.com" },
  { key: "remarks", label: "Remarks (if any)", ph: "" },
];

const PREVIEW_COLS = ["full_name", "joining_date", "room_no", "floor", "sharing_type", "monthly_rent", "security_deposit", "refundable_amount", "whatsapp", "email", "remarks"];
const PREVIEW_LABELS = ["Name", "Date of Joining", "Room", "Floor No", "Sharing", "Rent", "Deposit", "Refundable", "Mobile", "Email", "Remarks"];

const blankRow = () => ({ full_name: "", joining_date: "", room_no: "", sharing_type: "1", monthly_rent: "", security_deposit: "", refundable_amount: "", floor: "", whatsapp: "", email: "", remarks: "" });

function validateRow(r, label) {
  if (!r.full_name.trim()) return `${label} Full name is required`;
  if (r.full_name.trim().length > 30) return `${label} Full name is limited to 30 characters`;
  if (!r.room_no.trim()) return `${label} Room No is required`;
  if (r.joining_date && !DOJ_RE.test(r.joining_date.trim())) return `${label} Date of Joining must be in DD-MM-YYYY format`;
  if (r.sharing_type && !SHARING_OPTIONS.includes(r.sharing_type)) return `${label} Sharing type must be 1-5`;
  const dep = parseFloat(r.security_deposit) || 0;
  const ref = parseFloat(r.refundable_amount) || 0;
  if (ref > dep) return `${label} Refundable amount cannot exceed the deposit amount`;
  return null;
}

const toPayload = (rows) => rows.map((r) => ({
  full_name: r.full_name.trim().slice(0, 30),
  room_no: r.room_no.trim(),
  whatsapp: (r.whatsapp || "").trim() || null,
  email: (r.email || "").trim() || null,
  floor: (r.floor || "").trim() || null,
  sharing_type: r.sharing_type || null,
  remarks: (r.remarks || "").trim() || null,
  joining_date: (r.joining_date || "").trim() || null,
  monthly_rent: parseFloat(r.monthly_rent) || 0,
  security_deposit: parseFloat(r.security_deposit) || 0,
  refundable_amount: parseFloat(r.refundable_amount) || 0,
  deposit_refundable: true,
  active: true,
}));

const TEMPLATE_HEADER = "Full name,Date of Joining (DD-MM-YYYY),Room No,Floor Number,Sharing type (1-5),Rent amount,Deposit (Advance),Refundable amount,Mobile number,Email ID,Remarks";

export default function TenantBulkGrid({ onSaved }) {
  const [rows, setRows] = useState([blankRow(), blankRow(), blankRow()]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const setCell = (i, key, value) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
  const addRow = () => setRows((rs) => [...rs, blankRow()]);
  const removeRow = (i) => setRows((rs) => (rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs));

  const saveRows = async (filled, onOk) => {
    if (filled.length === 0) return toast.error("Enter at least one tenant row");
    for (const [i, r] of filled.entries()) {
      const err = validateRow(r, `Row ${i + 1}:`);
      if (err) return toast.error(err);
    }
    setBusy(true);
    try {
      await api.post("/tenants/bulk", toPayload(filled));
      toast.success(`${filled.length} tenant(s) added`);
      onOk();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (Array.isArray(d) ? d[0]?.msg : "Save failed"));
    } finally { setBusy(false); }
  };

  const saveAll = () =>
    saveRows(rows.filter((r) => Object.values(r).some((v) => String(v).trim() !== "")), () => {
      setRows([blankRow(), blankRow(), blankRow()]);
      onSaved?.();
    });

  const savePreview = () =>
    saveRows(preview, () => {
      setPreview(null);
      onSaved?.();
    });

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_HEADER + "\n"], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "tenant_upload_template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Template downloaded — fill it on your laptop and upload it back");
  };

  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const lines = String(reader.result || "").split(/\r?\n/).filter((l) => l.trim() !== "");
      const parsed = lines.slice(1).map((line) => {
        const c = line.split(",").map((x) => x.trim());
        return {
          full_name: c[0] || "", joining_date: c[1] || "", room_no: c[2] || "", floor: c[3] || "",
          sharing_type: (c[4] || "").replace(/^0+(?=\d)/, ""), monthly_rent: c[5] || "", security_deposit: c[6] || "",
          refundable_amount: c[7] || "", whatsapp: c[8] || "", email: c[9] || "", remarks: c[10] || "",
        };
      }).filter((r) => Object.values(r).some((v) => v !== ""));
      if (!parsed.length) return toast.error("No data rows found in the file");
      setPreview(parsed);
    };
    reader.readAsText(file);
  };

  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4" data-testid="tenant-bulk-grid">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="tenant-template-download-button">
          <Download className="h-4 w-4 mr-1.5" /> Download template (CSV)
        </Button>
        <input type="file" accept=".csv,text/csv" ref={fileRef} className="hidden" onChange={handleUpload} data-testid="tenant-template-upload-input" />
        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="tenant-template-upload-button">
          <Upload className="h-4 w-4 mr-1.5" /> Upload filled template
        </Button>
      </div>

      {preview && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50/50 p-4" data-testid="tenant-upload-preview">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <p className="text-sm font-semibold text-emerald-800">Preview — {preview.length} tenant(s) from file</p>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setPreview(null)} data-testid="tenant-preview-cancel-button">
                <X className="h-4 w-4 mr-1" /> Cancel
              </Button>
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={savePreview} disabled={busy} data-testid="tenant-preview-save-button">
                <Save className="h-4 w-4 mr-1.5" /> {busy ? "Saving…" : "Save to database"}
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
            <table className="w-full text-xs min-w-[950px]">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {PREVIEW_LABELS.map((h) => <th key={h} className="px-2 py-1.5 text-left font-semibold uppercase tracking-wider text-slate-500">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {preview.map((r, i) => {
                  const err = validateRow(r, "");
                  return (
                    <tr key={i} className={`border-t border-slate-100 ${err ? "bg-rose-50" : ""}`} title={err || "Looks good"} data-testid={`tenant-preview-row-${i}`}>
                      {PREVIEW_COLS.map((k) => <td key={k} className="px-2 py-1.5">{r[k] || "—"}</td>)}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">Rows highlighted in red have validation issues — hover to see the reason. Saving is blocked until they are fixed.</p>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm min-w-[1200px]">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {COLUMNS.map((c) => (
                <th key={c.key} className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  {c.label}
                  {c.sub && <span className="block font-normal normal-case tracking-normal text-slate-400">{c.sub}</span>}
                </th>
              ))}
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100" data-testid={`tenant-bulk-row-${i}`}>
                {COLUMNS.map((c) => (
                  <td key={c.key} className="p-1">
                    {c.select ? (
                      <select
                        value={r[c.key]}
                        onChange={(e) => setCell(i, c.key, e.target.value)}
                        className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                        data-testid={`tenant-bulk-${c.key}-${i}`}
                      >
                        {c.select.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input
                        type={c.type || "text"}
                        value={r[c.key]}
                        onChange={(e) => setCell(i, c.key, e.target.value)}
                        placeholder={c.ph}
                        maxLength={c.maxLength}
                        className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
                        data-testid={`tenant-bulk-${c.key}-${i}`}
                      />
                    )}
                  </td>
                ))}
                <td className="p-1">
                  <button onClick={() => removeRow(i)} className="h-9 w-9 flex items-center justify-center text-rose-500 hover:bg-rose-50 rounded-md" data-testid={`tenant-bulk-remove-${i}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={addRow} data-testid="tenant-bulk-add-row-button">
          <Plus className="h-4 w-4 mr-1" /> Add row
        </Button>
        <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={saveAll} disabled={busy} data-testid="tenant-bulk-save-button">
          <Save className="h-4 w-4 mr-1.5" /> {busy ? "Saving…" : "Save all tenants"}
        </Button>
      </div>
      <p className="mt-2 text-[11px] text-slate-500">Full name (max 30 chars) and Room No are required. Date of Joining must be DD-MM-YYYY. Refundable amount cannot exceed the deposit.</p>
    </div>
  );
}
